import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap, shareReplay } from 'rxjs/operators';
import { environment } from 'src/environments/environment.prod';
import { HeaderMenuData, HeadersDataInterface, SubCategoriesData } from 'src/_lib/interfaces/header.interface';
import { Common } from 'src/_lib/interfaces/common.interface';
import { BrowserService } from 'src/_lib/service/browser.service';
import { CommunicationService } from './communication.service';

@Injectable({
  providedIn: 'root',
})
export class HeaderService {
  endpoint: string = environment.apiUrl;
  data: any;
  cachedData$: Observable<Common<HeadersDataInterface>> | null = null;
  cachedMobileMenuData$: Observable<Common<HeaderMenuData>> | null = null;
  cachedTickerData$: Observable<any> | null = null;

  constructor(private http: HttpClient, private browser: BrowserService, private comm:CommunicationService) { }

  headerList(): Observable<Common<HeadersDataInterface>> {
    const storedData = this.getStoredHeaderData();
    if (storedData) {
      return of(storedData);
    }
    if (!this.cachedData$) {
      this.cachedData$ = this.http.get<Common<HeadersDataInterface>>(this.endpoint + "headers").pipe(
        tap(data => {
          this.storeHeaderData(data);
        }),
        shareReplay(1)
      );
    }
    return this.cachedData$;
  }

  storeHeaderData(data: Common<HeadersDataInterface>): void {
    this.browser.localStorage?.setItem('headerData', JSON.stringify(data));
    const headerdata = data?.[0].data;
    const vehicleSetting = headerdata.vehicleSettings?.[0]?.search_by_vehicle || '';
    const vehicleBtnFlag = {
      do_not_show: vehicleSetting === 'do_not_show',
      header_link: ['header_link', 'homepage_slider_header', 'header_sidebar', 'vehicle_search_all_pages'].includes(vehicleSetting),
      homepage_slider: ['homepage_slider', 'homepage_slider_header', 'vehicle_search_all_pages'].includes(vehicleSetting),
      homepage_slider_header: ['homepage_slider_header', 'vehicle_search_all_pages'].includes(vehicleSetting),
      vehicle_search_all_pages: vehicleSetting === 'vehicle_search_all_pages',
      header_sidebar: ['header_sidebar', 'vehicle_search_all_pages'].includes(vehicleSetting),
    };
    this.browser.localStorage?.setItem('searchByVehicleDisabled', JSON.stringify(vehicleBtnFlag));
    this.comm.inventoryDisabled.next(null);
    this.comm.showVehicleButton.next(null);
    this.comm.vehicleSearch_without_category.next(null);
    this.comm.cacheClear.next(null);
  }

  getStoredHeaderData(): Common<HeadersDataInterface> | null {
    const data = this.browser.localStorage?.getItem('headerData');
    return data ? JSON.parse(data) : null;
  }

  headerNavList(): Observable<Common<HeadersDataInterface>> {
    return this.http.get<Common<HeadersDataInterface>>(this.endpoint + "headers");
  }

  cartList(): Observable<Common<HeadersDataInterface>> {
    return this.http.get<Common<HeadersDataInterface>>(this.endpoint + "headers");
  }

  submitYourRidedata(userData: any): Observable<any> {
    return this.http.post(this.endpoint + 'submit_your_ride', userData);
  }

  headerNavCategoriesSubCategories(): Observable<Common<SubCategoriesData>> {
    return this.http.get<Common<SubCategoriesData>>(this.endpoint + 'categories-subcategories');
  }

  getDropdownData(): Observable<any> {
    return this.http.get(this.endpoint + 'dealers-list');
  }

  getTickersData(): Observable<any> {
    if (this.cachedTickerData$) {
      return this.cachedTickerData$;
    }
    const storedData = this.getStoredTickerData();
    if (storedData) {
      this.cachedTickerData$ = of(storedData).pipe(shareReplay(1));
      return this.cachedTickerData$;
    }
    this.cachedTickerData$ = this.http
      .get(this.endpoint + 'tickers')
      .pipe(
        tap(data => this.storeTickerData(data)),
        shareReplay(1)
      );
    return this.cachedTickerData$;
  }

  storeTickerData(data: any): void {
    this.browser.localStorage?.setItem('tickerData', JSON.stringify(data));
  }

  getStoredTickerData(): any | null {
    const data = this.browser.localStorage?.getItem('tickerData');
    return data ? JSON.parse(data) : null;
  }

  headerMenuList(): Observable<Common<HeaderMenuData>> {
    if (this.cachedMobileMenuData$) return this.cachedMobileMenuData$;
    const storedData = this.getStoredMobileMenuData();
    if (storedData) {
      this.cachedMobileMenuData$ = of(storedData).pipe(shareReplay(1));
      return this.cachedMobileMenuData$;
    }
    this.cachedMobileMenuData$ = this.http.get<Common<HeaderMenuData>>(this.endpoint + 'mobile-menu').pipe(
      tap(data => this.storeMobileMenuData(data)),
      shareReplay(1)
    );
    return this.cachedMobileMenuData$;
  }

  storeMobileMenuData(data: Common<HeaderMenuData>): void {
    this.browser.localStorage?.setItem('mobileMenuData', JSON.stringify(data));
  }

  getStoredMobileMenuData(): Common<HeaderMenuData> | null {
    const data = this.browser.localStorage?.getItem('mobileMenuData');
    return data ? JSON.parse(data) : null;
  }

  getloginPageimage(): Observable<any> {
    if (this.data) {
      return of(this.data);
    } else {
      return this.http.get(this.endpoint + 'headers').pipe(
        tap(response => {
          this.data = response;
        })
      );
    }
  }
}